import React, { useEffect, useRef, useState } from 'react';
import { FaDownload, FaTimes, FaFileImage } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import logo from '../../assets/Images/LOGOS/Docity_Logo.png';
import './MarketValueCertificate.css';

const MarketValueCertificate = ({ isOpen, onClose, certificateData }) => {
    const certificateRef = useRef(null);
    const [userDetails, setUserDetails] = useState({});
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        try {
            const userDetails2 = JSON.parse(localStorage.getItem('userDetails')) || {};
            setUserDetails(userDetails2);
        } catch (e) {
            console.error('Error reading userDetails:', e);
        }
    }, []);

    if (!isOpen || !certificateData) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return dateString || 'N/A';
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const generateCertificateNumber = () => {
        const timestamp = Date.now().toString(36).toUpperCase();
        const random = Math.random().toString(36).substr(2, 8).toUpperCase();
        return `MVDC-${timestamp}-${random}`;
    };

    const captureCanvas = async () => {
        if (!certificateRef.current) return null;

        try {
            return await html2canvas(certificateRef.current, {
                scale: 2,
                useCORS: false,
                allowTaint: false,
                logging: false,
                backgroundColor: '#ffffff',
                ignoreElements: (node) => node?.classList?.contains('mvec-download-overlay'),
                onclone: (clonedDoc) => {
                    const clonedElement = clonedDoc.querySelector('.mvec-certificate-container');
                    if (clonedElement) {
                        clonedElement.style.margin = '0';
                        clonedElement.style.transform = 'none';
                        clonedElement.style.boxShadow = 'none';
                    }
                    const overlays = clonedDoc.querySelectorAll('.mvec-download-overlay');
                    overlays.forEach((el) => {
                        el.style.display = 'none';
                    });
                },
            });
        } catch (err) {
            console.error('Primary capture failed:', err);
            try {
                return await html2canvas(certificateRef.current, {
                    scale: 1,
                    useCORS: false,
                    allowTaint: false,
                    logging: false,
                    backgroundColor: '#ffffff',
                    ignoreElements: (node) => node?.classList?.contains('mvec-download-overlay'),
                });
            } catch (fallbackErr) {
                console.error('Fallback capture failed:', fallbackErr);
                throw fallbackErr;
            }
        }
    };

    const handleDownloadPDF = async () => {
        if (isDownloading) return;
        setIsDownloading(true);

        try {
            const canvas = await captureCanvas();
            if (!canvas || !canvas.width || !canvas.height) {
                throw new Error('Could not render certificate canvas');
            }

            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            const PdfConstructor = typeof jsPDF === 'function' ? jsPDF : jsPDF.jsPDF;
            const pdf = new PdfConstructor('p', 'mm', 'a4');

            const pageWidth = pdf.internal.pageSize.getWidth() || 210;
            const pageHeight = pdf.internal.pageSize.getHeight() || 297;
            const margin = 10;
            const imgWidth = Math.max(1, pageWidth - margin * 2);

            let calculatedHeight = (canvas.height * imgWidth) / canvas.width;
            if (Number.isNaN(calculatedHeight) || !Number.isFinite(calculatedHeight) || calculatedHeight <= 0) {
                calculatedHeight = pageHeight - margin * 2;
            }

            const finalHeight = Math.min(calculatedHeight, pageHeight - margin * 2);

            pdf.addImage(imgData, 'JPEG', margin, margin, imgWidth, finalHeight);
            pdf.save(`Market_Value_Declaration_${generateCertificateNumber()}.pdf`);
        } catch (error) {
            console.error('PDF Download Error:', error);
            alert(`Failed to download PDF: ${error.message || 'Please try again'}`);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDownloadImage = async () => {
        if (isDownloading) return;
        setIsDownloading(true);

        try {
            const canvas = await captureCanvas();
            if (!canvas || !canvas.width || !canvas.height) {
                throw new Error('Could not render certificate canvas');
            }

            const imgData = canvas.toDataURL('image/png', 1.0);
            const link = document.createElement('a');
            link.href = imgData;
            link.download = `Market_Value_Declaration_${generateCertificateNumber()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Image Download Error:', error);
            alert(`Failed to download image: ${error.message || 'Please try again'}`);
        } finally {
            setIsDownloading(false);
        }
    };

    const certificateNumber = generateCertificateNumber();
    const currentDate = new Date().toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });

    const generateUniqueSealCode = (certificateId, ownerName, issuedDate) => {
        const source = `${certificateId || 'DOCITY'}|${ownerName || 'OWNER'}|${issuedDate || new Date().toISOString()}`;
        let hash = 0;
        for (let i = 0; i < source.length; i += 1) {
            hash = (hash << 5) - hash + source.charCodeAt(i);
            hash |= 0;
        }
        const encoded = Math.abs(hash).toString(36).toUpperCase().padStart(8, '0').slice(0, 8);
        return `DC-${encoded}`;
    };

    const ownerName = userDetails?.name || userDetails?.userName || certificateData?.ownerName || 'User';
    const socSealCode = generateUniqueSealCode(certificateNumber, ownerName, currentDate);

    return (
        <div className="mvec-modal-overlay" onClick={onClose}>
            <div className="mvec-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="mvec-modal-header">
                    <button className="mvec-close-btn" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>
                <div className="mvec-certificate-container" ref={certificateRef}>
                    <div className="mvec-download-overlay" onClick={handleDownloadPDF} title="Download PDF">
                        <FaDownload />
                    </div>
                    <div className="mvec-cert-header">
                        <img src={logo} alt="Docity Logo" className="mvec-cert-logo" />
                        <div className="mvec-cert-header-text">
                            <h1 className="mvec-cert-title">Market Value Declaration</h1>
                        </div>
                    </div>

                    <div className="mvec-cert-number">
                        <span>Date : {currentDate}</span>
                    </div>

                    <div className="mvec-cert-divider" />

                    <div className="mvec-cert-content">
                        <p className="mvec-certificate-text">
                            I <span className='certText'>{ownerName}</span> declare the market value of the property <span className='certText'>{certificateData?.propertyAddress || certificateData?.address || 'N/A'}</span> purchased with registration Date: <span className='certText'>{formatDate(certificateData?.registrationDate)}</span> and Document number: <span className='certText'>{certificateData?.registrationDocNumber || certificateData?.regDocNumber || 'N/A'}</span> hereby declare the market value: <span className='certText'>{certificateData?.currency || 'INR'} {certificateData?.marketValue || 'N/A'}</span> based on my investment and present day local demand.
                        </p>

                        <div className="mvec-certificate-bottom-row">
                            <div className="mvec-seal-section">
                                <div className="soc-oc-seal-wrap">
                                    <div className="soc-oc-seal-circle" aria-label={`Unique seal ${socSealCode}`}>
                                        <div className="soc-seal-inner-ring">
                                            <span className="soc-seal-top-text">DOCITY.CO.IN</span>
                                            <img src={logo} alt="Docity Seal Logo" className="soc-seal-logo-img" />
                                            <span className="soc-seal-center-text">DOCITY SEAL</span>
                                            <span className="soc-seal-bottom-text">THE DOCITY SEAL</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mvec-signature-section">
                                <div className="mvec-signature-line"></div>
                                <div className="mvec-signature-name" style={{
                                    fontFamily: "'Brush Script MT', 'Dancing Script', 'Georgia', cursive, serif",
                                    fontSize: '26px',
                                    fontWeight: '500',
                                    fontStyle: 'italic',
                                    color: '#0f4c81',
                                    marginBottom: '2px',
                                    whiteSpace: 'nowrap'
                                }}>DoCity</div>
                                <div className="mvec-signature-label">Authorised Signature</div>
                            </div>
                        </div>
                    </div>

                    <div className="mvec-cert-footer">
                        <p className="mvec-footer-text">Docity does not take the responsibility of title and posesssion of this property.</p>
                    </div>
                </div>

                <div className="mvec-actions" style={{ padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button className="mvec-btn mvec-btn-secondary" onClick={onClose} disabled={isDownloading}>
                        Close
                    </button>
                    <button className="mvec-btn mvec-btn-secondary" onClick={handleDownloadImage} disabled={isDownloading}>
                        <FaFileImage /> {isDownloading ? 'Generating...' : 'Download Image'}
                    </button>
                    <button className="mvec-btn mvec-btn-primary" onClick={handleDownloadPDF} disabled={isDownloading}>
                        <FaDownload /> {isDownloading ? 'Generating PDF...' : 'Download PDF'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MarketValueCertificate;
